import rules from '../../constants/businessRules/businessRules';
import emitter from './emitter';

/**
 * This is a one-to-one mapping to the callbacks that are exported by `bt-pikasso`.
 * @module Component Callbacks
 */

/**
 * This function will invoke the `onMount()` callback defined by the integration that is consuming Pikasso.
 * Will be triggered after Pikasso's initial set up e.g. session and user data validation
 */
const emitOnMount = () => {
    emitter.emit(rules.EVENT_EMITTER_EVENTS.MOUNTED);
};

/**
 * This function will invoke the `onNotification()` callback defined by the integration that is consuming Pikasso.
 * Will be triggered when Pikasso wants to send a message as notification to the user.
 *
 * @param {Object} payload - payload object that will be passed to the integration callback
 * @param {string} payload.msg - message verbiage
 */
const emitOnNotification = (payload) => {
    emitter.emit(rules.EVENT_EMITTER_EVENTS.NOTIFICATION, payload);
};

/**
 * This function will invoke the `onError()` callback defined by the integration that is consuming Pikasso.
 * Will be triggered when Pikasso wants to inform the integration that an error has occurred.
 *
 * @param {Object} payload - payload object that will be passed to the integration callback
 * @param {string} payload.msg - error verbiage
 */
const emitOnError = (payload) => {
    emitter.emit(rules.EVENT_EMITTER_EVENTS.ERROR, payload);
};

/**
 * This function will invoke the `onSave()` callback defined by the integration that is consuming Pikasso.
 * Will be triggered when a save has been trigged either manually or automatically (timer).
 *
 * @param {Object} payload - payload object that will be passed to the integration callback
 * @param {string} payload.html - html content as string
 * @param {string} payload.blocks - blocks content as string
 */
const emitOnSave = (payload) => {
    emitter.emit(rules.EVENT_EMITTER_EVENTS.SAVE, payload);
};

/**
 * This function will invoke the `onSaveAsTemplate()` callback defined by the integration that is consuming Pikasso.
 * Will be triggered when a save as template has been trigged.
 *
 * @param {Object} payload - payload object that will be passed to the integration callback
 * @param {string} payload.html - html content as string
 * @param {string} payload.blocks - blocks content as string
 */
const emitOnSaveAsTemplate = (payload) => {
    emitter.emit(rules.EVENT_EMITTER_EVENTS.SAVE_AS_TEMPLATE, payload);
};

/**
 * This function will invoke the `onSaveBlock()` callback defined by the integration that is consuming Pikasso.
 * Currently this is not being used in Pikasso, this callback is disabled.
 *
 * @param {Object} payload - payload object that will be passed to the integration callback
 * @param {string} payload.blocks - blocks content as string
 */
const emitOnSaveBlock = (payload) => {
    emitter.emit(rules.EVENT_EMITTER_EVENTS.SAVE_BLOCK, payload);
};

/**
 * This function will invoke the `onDownload()` callback defined by the integration that is consuming Pikasso.
 * Currently this is not being used in Pikasso, this callback is disabled.
 *
 * @param {Object} payload - payload object that will be passed to the integration callback
 * @param {string} payload.type - type of the content: ['html', 'pdf']
 * @param {string} payload.data - content data as string
 */
const emitOnDownload = (payload) => {
    emitter.emit(rules.EVENT_EMITTER_EVENTS.DOWNLOAD, payload);
};

// TODO: as a tech debt, this should follow the same pattern as the other callbacks payload { html }
/**
 * This function will invoke the `onSendTest()` callback defined by the integration that is consuming Pikasso.
 * Will be triggered when a send test email wants to be sent.
 *
 * @param {string} html - html content as string
 */
const emitOnSendTest = (html) => {
    emitter.emit(rules.EVENT_EMITTER_EVENTS.SEND_TEST, html);
};

// TODO: as a tech debt, this should follow the same pattern as the other callbacks payload { status }
/**
 * This function will invoke the `onChangesExist()` callback defined by the integration that is consuming Pikasso.
 * Will be triggered to inform the parent window that there are changes in the draft that are not yet saved.
 * It has been used to show the exit message.
 *
 * @param {boolean} status - status informing if there are changes in the draft
 */
const emitOnChangesExist = (status) => {
    emitter.emit(rules.EVENT_EMITTER_EVENTS.CHANGES_EXIST_STATUS, status);
};

/**
 * This function will invoke the `onPersonalizationEdit()` callback defined by the integration (member-site only) that is consuming Pikasso.
 * Will be triggered when the user wants to add/edit a MMv2 fallbacks
 */
const emitOnPersonalizationEdit = () => {
    emitter.emit(rules.EVENT_EMITTER_EVENTS.PERSONALIZATION_EDIT);
};

/**
 * This function will invoke the `onPersonalizationDownloadCsv()` callback defined by the integration (member-site only) that is consuming Pikasso.
 * Will be triggered when the user wants to download the csv that was uploaded into personalization one time use tags
 */
const emitOnPersonalizationDownloadCsv = () => {
    emitter.emit(rules.EVENT_EMITTER_EVENTS.PERSONALIZATION_DOWNLOAD_CSV);
};

/**
 * This function will invoke the `onGetSingleUseTagData()` callback defined by the integration (member-site only) this is consuming Pikasso.
 * Will be triggered when we want to fetch single use tag data.
 */
const emitOnGetSingleUseTagData = () => {
    emitter.emit(rules.EVENT_EMITTER_EVENTS.GET_SINGLE_USE_TAG_DATA);
};

export {
    emitOnMount,
    emitOnNotification,
    emitOnError,
    emitOnSave,
    emitOnSaveAsTemplate,
    emitOnSaveBlock,
    emitOnDownload,
    emitOnSendTest,
    emitOnChangesExist,
    emitOnPersonalizationEdit,
    emitOnPersonalizationDownloadCsv,
    emitOnGetSingleUseTagData,
};
